import { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DashboardLayout } from '@/components/DashboardLayout';
import { getArgusCatalog, refreshArgusCatalog, getArgusRefreshStatus, getArgusHistory, forceArgusSnapshot, getArgusQueries, addArgusQuery, removeArgusQuery, getArgusManualPrices, upsertArgusManualPrice, deleteArgusManualPrice, getPhoneB2BConfig, getModelsWithoutArgus, getModelMappings, upsertModelMapping, deleteModelMapping } from '@/services/api';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Loader2, ChevronDown, ChevronRight, ExternalLink, BarChart3, Smartphone, HardDrive, RefreshCw, Clock, Filter, TrendingUp, TrendingDown, Minus, Camera, Settings, Plus, X, Pencil, Save, Tag, Link, AlertTriangle, Search, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const GRADE_COLORS = {
  'Comme neuf': { text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-800', stroke: '#059669' },
  'Très bon état': { text: 'text-sky-700', badge: 'bg-sky-100 text-sky-800', stroke: '#0284c7' },
  'Bon état': { text: 'text-amber-700', badge: 'bg-amber-100 text-amber-800', stroke: '#d97706' },
  'État correct': { text: 'text-orange-700', badge: 'bg-orange-100 text-orange-800', stroke: '#ea580c' },
  'Reconditionné': { text: 'text-slate-600', badge: 'bg-slate-100 text-slate-700', stroke: '#475569' },
  'Non spécifié': { text: 'text-gray-500', badge: 'bg-gray-100 text-gray-600', stroke: '#9ca3af' },
};
const DEFAULT_GRADE_COLORS = { text: 'text-gray-500', badge: 'bg-gray-100 text-gray-600', stroke: '#9ca3af' };

function EvoBadge({ value }) {
  if (value === null || value === undefined) return <span className="text-[10px] text-slate-300">-</span>;
  const isUp = value > 0;
  const isFlat = Math.abs(value) < 0.5;
  const Icon = isFlat ? Minus : isUp ? TrendingUp : TrendingDown;
  const color = isFlat ? 'text-slate-400' : isUp ? 'text-red-500' : 'text-emerald-600';
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${color}`}>
      <Icon className="w-3 h-3" />{isFlat ? '0%' : `${value > 0 ? '+' : ''}${value}%`}
    </span>
  );
}

function PriceHistoryChart({ model, storage, grades }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getArgusHistory(model, storage || undefined);
        if (!cancelled) setHistory(data);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [model, storage]);

  if (loading) return <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  const storageData = history?.by_storage?.[storage || '-'] || history?.by_storage?.[Object.keys(history?.by_storage || {})[0]] || [];
  const evo = history?.evolutions?.[storage || '-'] || history?.evolutions?.[Object.keys(history?.evolutions || {})[0]] || {};

  if (storageData.length < 2) {
    return (
      <div className="py-4 px-2 text-center text-xs text-slate-400">
        Pas encore assez de donnees pour afficher l'historique. Les releves sont enregistres chaque semaine.
      </div>
    );
  }

  // Build chart data with one line per grade + global avg
  const chartData = storageData.map(pt => {
    const row = { date: pt.date.slice(5), fullDate: pt.date, avg: pt.avg_price };
    for (const [g, gd] of Object.entries(pt.by_grade || {})) {
      row[g] = gd.avg;
    }
    return row;
  });

  const availableGrades = [...new Set(storageData.flatMap(pt => Object.keys(pt.by_grade || {})))];

  return (
    <div className="space-y-3 px-2 py-3">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-slate-700">Evolution des prix — {model} {storage}</h4>
        <div className="flex items-center gap-3">
          {evo.week_change !== null && <div className="text-xs text-slate-500">Semaine: <EvoBadge value={evo.week_change} /></div>}
          {evo.month_change !== null && <div className="text-xs text-slate-500">Mois: <EvoBadge value={evo.month_change} /></div>}
        </div>
      </div>
      <div className="h-[180px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#94a3b8" />
            <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" domain={['auto', 'auto']} />
            <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0' }}
              formatter={(v, name) => [`${v} EUR`, name === 'avg' ? 'Prix ref.' : name]}
              labelFormatter={(l, payload) => payload?.[0]?.payload?.fullDate || l} />
            <Line type="monotone" dataKey="avg" stroke="#0f172a" strokeWidth={2} dot={{ r: 3 }} name="Prix ref." />
            {availableGrades.map(g => (
              <Line key={g} type="monotone" dataKey={g} stroke={(GRADE_COLORS[g] || DEFAULT_GRADE_COLORS).stroke}
                strokeWidth={1.5} dot={{ r: 2 }} strokeDasharray="4 2" name={g} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function VendorDetails({ vendors, model, storage, grades }) {
  return (
    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2 }} className="overflow-hidden">
      <div className="border-b border-slate-100">
        <PriceHistoryChart model={model} storage={storage} grades={grades} />
      </div>
      <div className="px-4 pb-4 pt-2">
        <h4 className="text-xs font-semibold text-slate-700 mb-2">Detail vendeurs</h4>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="py-2 px-2 text-left font-medium text-slate-500">Vendeur</th>
              <th className="py-2 px-2 text-left font-medium text-slate-500">Produit</th>
              <th className="py-2 px-2 text-center font-medium text-slate-500">Grade</th>
              <th className="py-2 px-2 text-right font-medium text-slate-500">Prix</th>
              <th className="py-2 px-2 text-center font-medium text-slate-500">Lien</th>
            </tr>
          </thead>
          <tbody>
            {vendors.map((v) =>
              v.offers.map((offer, oi) => (
                <tr key={`${v.seller}-${oi}`} className="border-b border-slate-100 hover:bg-slate-50/80">
                  <td className="py-1.5 px-2 font-medium text-slate-700 whitespace-nowrap">{oi === 0 ? v.seller : ''}</td>
                  <td className="py-1.5 px-2 text-slate-600 max-w-[300px]"><span className="line-clamp-1">{offer.name}</span></td>
                  <td className="py-1.5 px-2 text-center">
                    <Badge className={`text-[10px] border-0 ${(GRADE_COLORS[offer.quality] || DEFAULT_GRADE_COLORS).badge}`}>{offer.quality}</Badge>
                  </td>
                  <td className="py-1.5 px-2 text-right font-semibold text-slate-800">{offer.price} EUR</td>
                  <td className="py-1.5 px-2 text-center">
                    {offer.url && <a href={offer.url} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700"><ExternalLink className="w-3 h-3 inline" /></a>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
}

function ArgusLine({ line, displayGrades, isExpanded, onToggle, index, allGrades }) {
  return (
    <>
      <motion.tr initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index * 0.015, 0.5) }}
        className={`border-b border-slate-200 cursor-pointer transition-colors ${isExpanded ? 'bg-emerald-50/50' : 'hover:bg-slate-50'}`}
        onClick={onToggle} data-testid={`argus-line-${line.model}-${line.storage}`.replace(/\s+/g, '-').toLowerCase()}>
        <td className="py-2.5 px-4">
          <div className="flex items-center gap-1.5">
            {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
            <span className="font-semibold text-slate-800 text-sm">{line.model}</span>
            {(line.source === 'manual' || line.source === 'manual_override') && (
              <Badge className="bg-violet-100 text-violet-700 border-0 text-[9px] ml-1">Manuel</Badge>
            )}
          </div>
        </td>
        <td className="py-2.5 px-3 text-center"><span className="text-sm text-slate-600 font-medium">{line.storage || '-'}</span></td>
        {displayGrades.map(g => {
          const gs = line.by_grade[g];
          return (
            <td key={g} className="py-2.5 px-2 text-center">
              {gs ? (
                <div>
                  <span className={`text-sm font-bold ${(GRADE_COLORS[g] || DEFAULT_GRADE_COLORS).text}`}>{gs.avg} EUR</span>
                  <span className="text-[10px] text-slate-400 block">{gs.count}</span>
                </div>
              ) : <span className="text-slate-300">-</span>}
            </td>
          );
        })}
        <td className="py-2.5 px-3 text-center">
          <span className="text-sm font-bold text-slate-900">{line.avg_price} EUR</span>
          <span className="text-[10px] text-slate-400 block">{line.offer_count}</span>
        </td>
      </motion.tr>
      {isExpanded && (
        <tr><td colSpan={displayGrades.length + 3} className="p-0 bg-white border-b border-slate-200">
          <AnimatePresence>
            <VendorDetails vendors={line.vendors} model={line.model} storage={line.storage} grades={allGrades} />
          </AnimatePresence>
        </td></tr>
      )}
    </>
  );
}

export default function ArgusPage() {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [brandFilter, setBrandFilter] = useState('all');
  const [modelFilter, setModelFilter] = useState('all');
  const [storageFilter, setStorageFilter] = useState('all');
  const [gradeFilter, setGradeFilter] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const loadCatalog = useCallback(async () => {
    try {
      const data = await getArgusCatalog();
      setCatalog(data);
      if (!data.lines?.length && data.status === 'empty') handleRefresh();
    } catch { toast.error('Erreur chargement catalogue'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshArgusCatalog();
      toast.info('Rafraichissement lance...');
      const poll = setInterval(async () => {
        try {
          const st = await getArgusRefreshStatus();
          if (st.status === 'done') {
            clearInterval(poll);
            await loadCatalog();
            setRefreshing(false);
            toast.success(`Catalogue mis a jour : ${st.total_offers} offres, ${st.total_lines} modeles`);
          } else if (st.status === 'error') { clearInterval(poll); setRefreshing(false); toast.error('Erreur rafraichissement'); }
        } catch { /* polling */ }
      }, 3000);
    } catch (err) {
      setRefreshing(false);
      if (err.response?.data?.status === 'already_running') toast.info('Rafraichissement deja en cours...');
      else toast.error('Erreur');
    }
  };

  const handleSnapshot = async () => {
    try {
      const res = await forceArgusSnapshot();
      if (res.status === 'saved') toast.success(`Snapshot enregistre (${res.date})`);
      else if (res.status === 'exists') toast.info(res.message);
    } catch { toast.error('Erreur snapshot'); }
  };

  const toggleRow = (key) => {
    setExpandedRows(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };

  const availableBrands = useMemo(() => catalog?.brands || [], [catalog]);
  const availableModels = useMemo(() => {
    if (!catalog?.lines) return [];
    let lines = catalog.lines;
    if (brandFilter !== 'all') lines = lines.filter(l => l.brand === brandFilter);
    return [...new Set(lines.map(l => l.model))].sort();
  }, [catalog, brandFilter]);
  const availableStorages = useMemo(() => {
    if (!catalog?.lines) return [];
    let lines = catalog.lines;
    if (brandFilter !== 'all') lines = lines.filter(l => l.brand === brandFilter);
    if (modelFilter !== 'all') lines = lines.filter(l => l.model === modelFilter);
    const set = new Set(lines.map(l => l.storage).filter(Boolean));
    return [...set].sort((a, b) => { try { return parseInt(a) - parseInt(b); } catch { return 0; } });
  }, [catalog, brandFilter, modelFilter]);

  const hasActiveFilter = brandFilter !== 'all' || modelFilter !== 'all' || storageFilter !== 'all' || gradeFilter !== null;
  const resetFilters = () => { setBrandFilter('all'); setModelFilter('all'); setStorageFilter('all'); setGradeFilter(null); };
  const onBrandChange = (v) => { setBrandFilter(v); setModelFilter('all'); setStorageFilter('all'); };
  const onModelChange = (v) => { setModelFilter(v); setStorageFilter('all'); };

  const filteredLines = useMemo(() => {
    if (!catalog?.lines) return [];
    let lines = catalog.lines;
    if (brandFilter !== 'all') lines = lines.filter(l => l.brand === brandFilter);
    if (modelFilter !== 'all') lines = lines.filter(l => l.model === modelFilter);
    if (storageFilter !== 'all') lines = lines.filter(l => l.storage === storageFilter);
    if (gradeFilter) lines = lines.filter(l => l.by_grade[gradeFilter]);
    if (gradeFilter) lines = lines.map(l => ({ ...l, avg_price: l.by_grade[gradeFilter]?.avg || l.avg_price, offer_count: l.by_grade[gradeFilter]?.count || l.offer_count }));
    return lines;
  }, [catalog, brandFilter, modelFilter, storageFilter, gradeFilter]);

  const grades = catalog?.grades || [];
  const displayGrades = gradeFilter ? [gradeFilter] : grades;
  const totalFilteredOffers = filteredLines.reduce((s, l) => s + l.offer_count, 0);

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' a ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="argus-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Argus</h1>
            <p className="text-slate-600 mt-1">Tarifs de reference du marche reconditionne</p>
          </div>
          <div className="flex items-center gap-2">
            {catalog?.last_updated && (
              <div className="flex items-center gap-1.5 text-xs text-slate-500 bg-slate-100 px-3 py-1.5 rounded-full" data-testid="last-updated">
                <Clock className="w-3.5 h-3.5" />
                <span>MAJ : {formatDate(catalog.last_updated)}</span>
              </div>
            )}
            <Button onClick={handleSnapshot} variant="outline" size="sm" className="gap-1.5 text-xs" data-testid="snapshot-btn">
              <Camera className="w-3.5 h-3.5" /> Snapshot
            </Button>
            <Button onClick={() => setShowSettings(!showSettings)} variant={showSettings ? "default" : "outline"} size="sm" className="gap-1.5 text-xs" data-testid="settings-btn">
              <Settings className="w-3.5 h-3.5" /> Parametres
            </Button>
            <Button onClick={handleRefresh} disabled={refreshing} variant="outline" size="sm" className="gap-1.5" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'MAJ...' : 'Actualiser'}
            </Button>
          </div>
        </div>

        {(loading || (refreshing && !catalog?.lines?.length)) && (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="w-10 h-10 animate-spin text-emerald-500" />
            <p className="text-slate-500">{refreshing ? 'Chargement du catalogue... (30-60s)' : 'Chargement...'}</p>
          </div>
        )}

        {showSettings && <ArgusSettings onRefresh={handleRefresh} onClose={() => setShowSettings(false)} catalogLines={catalog?.lines || []} />}

        {!loading && catalog?.lines?.length > 0 && (
          <>
            {/* Filters */}
            <Card className="border-slate-200">
              <CardContent className="pt-3 pb-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <Filter className="w-4 h-4 text-slate-400 shrink-0" />
                  {hasActiveFilter && (
                    <button onClick={resetFilters}
                      className="text-xs px-2.5 py-1 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                      data-testid="reset-filters-btn">
                      Reinitialiser
                    </button>
                  )}
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-slate-500 font-medium">Marque</span>
                    <Select value={brandFilter} onValueChange={onBrandChange}>
                      <SelectTrigger className="h-8 w-[140px] text-xs" data-testid="brand-filter"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Toutes</SelectItem>
                        {availableBrands.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-slate-500 font-medium">Modele</span>
                    <Select value={modelFilter} onValueChange={onModelChange}>
                      <SelectTrigger className="h-8 w-[180px] text-xs" data-testid="model-filter"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        {availableModels.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-slate-500 font-medium">Stockage</span>
                    <Select value={storageFilter} onValueChange={setStorageFilter}>
                      <SelectTrigger className="h-8 w-[120px] text-xs" data-testid="storage-filter"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        {availableStorages.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="h-6 w-px bg-slate-200 mx-1" />
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs text-slate-500 font-medium">Grade</span>
                    <button onClick={() => setGradeFilter(null)}
                      className={`text-xs px-2.5 py-1 rounded-full transition-colors ${!gradeFilter ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      data-testid="grade-filter-all">Tous</button>
                    {grades.map(g => (
                      <button key={g} onClick={() => setGradeFilter(gradeFilter === g ? null : g)}
                        className={`text-xs px-2.5 py-1 rounded-full transition-colors ${gradeFilter === g ? 'bg-emerald-500 text-white' : `${(GRADE_COLORS[g] || DEFAULT_GRADE_COLORS).badge} hover:opacity-80`}`}
                        data-testid={`grade-filter-${g.replace(/\s+/g, '-').toLowerCase()}`}>{g}</button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <Card className="border-0 bg-slate-100 text-slate-700">
                <CardContent className="py-3 px-4 flex items-center gap-3">
                  <BarChart3 className="w-5 h-5 opacity-60" />
                  <div><p className="text-2xl font-bold">{totalFilteredOffers}</p><p className="text-xs opacity-60">Offres</p></div>
                </CardContent>
              </Card>
              <Card className="border-0 bg-emerald-50 text-emerald-700">
                <CardContent className="py-3 px-4 flex items-center gap-3">
                  <Smartphone className="w-5 h-5 opacity-60" />
                  <div><p className="text-2xl font-bold">{filteredLines.length}</p><p className="text-xs opacity-60">Modeles</p></div>
                </CardContent>
              </Card>
              <Card className="border-0 bg-blue-50 text-blue-700">
                <CardContent className="py-3 px-4 flex items-center gap-3">
                  <HardDrive className="w-5 h-5 opacity-60" />
                  <div><p className="text-2xl font-bold">{displayGrades.length}</p><p className="text-xs opacity-60">Grades</p></div>
                </CardContent>
              </Card>
            </div>

            {/* Table */}
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Tarifs de reference
                  {gradeFilter && <span className="text-emerald-600 ml-2">— {gradeFilter}</span>}
                  {brandFilter !== 'all' && <span className="text-blue-600 ml-2">— {brandFilter}</span>}
                  {modelFilter !== 'all' && <span className="text-purple-600 ml-2">— {modelFilter}</span>}
                  {storageFilter !== 'all' && <span className="text-orange-600 ml-2">— {storageFilter}</span>}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="argus-table">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50/80">
                        <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 w-[200px]">Modele</th>
                        <th className="py-2.5 px-3 text-center text-xs font-medium text-slate-500 w-[80px]">Go</th>
                        {displayGrades.map(g => (
                          <th key={g} className="py-2.5 px-2 text-center text-xs font-medium text-slate-500">
                            <span className={`inline-block px-2 py-0.5 rounded ${(GRADE_COLORS[g] || DEFAULT_GRADE_COLORS).badge}`}>{g}</span>
                          </th>
                        ))}
                        <th className="py-2.5 px-3 text-center text-xs font-semibold text-slate-700 bg-slate-100/80">Prix ref.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLines.map((line, idx) => {
                        const key = `${line.model}-${line.storage}`;
                        return <ArgusLine key={key} line={line} displayGrades={displayGrades} allGrades={grades}
                          isExpanded={expandedRows.has(key)} onToggle={() => toggleRow(key)} index={idx} />;
                      })}
                      {filteredLines.length === 0 && (
                        <tr><td colSpan={displayGrades.length + 3} className="py-12 text-center text-slate-400">Aucun resultat pour ces filtres</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {!loading && !refreshing && catalog?.lines?.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <BarChart3 className="w-12 h-12 text-slate-300" />
            <p className="text-slate-500">Le catalogue est vide</p>
            <Button onClick={handleRefresh} className="bg-emerald-500 hover:bg-emerald-600 gap-2"><RefreshCw className="w-4 h-4" /> Charger le catalogue</Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}


function ArgusSettings({ onRefresh, onClose, catalogLines }) {
  const [queries, setQueries] = useState([]);
  const [newQuery, setNewQuery] = useState('');
  const [manualPrices, setManualPrices] = useState([]);
  const [newManual, setNewManual] = useState({ brand: '', model: '', storage: '', price: '' });
  const [editingIdx, setEditingIdx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Phone B2B
  const [pb2bConfigured, setPb2bConfigured] = useState(false);
  const [missingModels, setMissingModels] = useState([]);
  const [loadingMissing, setLoadingMissing] = useState(false);
  // Mappings
  const [mappings, setMappings] = useState([]);
  const [matchingModel, setMatchingModel] = useState(null); // which pb2b model is being matched
  const [selectedArgus, setSelectedArgus] = useState('');
  const [argusSearch, setArgusSearch] = useState('');
  // Tab
  const [activeTab, setActiveTab] = useState('matching');
  // Search filter for PB2B unmatched models
  const [pb2bSearch, setPb2bSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [qRes, mRes, cfgRes, mapRes] = await Promise.all([
          getArgusQueries(), getArgusManualPrices(), getPhoneB2BConfig(), getModelMappings()
        ]);
        setQueries(qRes.queries || []);
        setManualPrices(mRes.prices || []);
        setPb2bConfigured(cfgRes.configured);
        setMappings(mapRes.mappings || []);
        if (cfgRes.configured) fetchMissing();
      } catch { toast.error('Erreur chargement parametres'); }
      finally { setLoading(false); }
    })();
  }, []);

  const fetchMissing = async () => {
    setLoadingMissing(true);
    try {
      const res = await getModelsWithoutArgus();
      setMissingModels(res.models || []);
    } catch (e) {
      if (e.response?.status !== 400) toast.error('Erreur chargement modeles Phone B2B');
    } finally { setLoadingMissing(false); }
  };

  // Mapping helpers
  const getMapping = (m) => mappings.find(mp => mp.pb2b_model === m.model && mp.pb2b_storage === (m.storage || ''));
  const getManualPrice = (m) => manualPrices.find(mp => mp.model.toLowerCase() === m.model.toLowerCase() && (mp.storage || '').toLowerCase() === (m.storage || '').toLowerCase());
  const getArgusPrice = (argusModel, argusStorage) => {
    return catalogLines.find(l => l.model.toLowerCase() === argusModel.toLowerCase() && (l.storage || '').toLowerCase() === (argusStorage || '').toLowerCase());
  };

  const handleMatch = async (pb2bModel) => {
    if (!selectedArgus) { toast.error('Selectionnez un modele Argus'); return; }
    // Parse selected: "model|||storage"
    const [argModel, argStorage] = selectedArgus.split('|||');
    try {
      await upsertModelMapping({
        pb2b_model: pb2bModel.model,
        pb2b_storage: pb2bModel.storage || '',
        pb2b_brand: pb2bModel.brand || '',
        argus_model: argModel,
        argus_storage: argStorage || '',
      });
      const res = await getModelMappings();
      setMappings(res.mappings || []);
      setMatchingModel(null);
      setSelectedArgus('');
      setArgusSearch('');
      toast.success(`${pb2bModel.model} → ${argModel} matche`);
    } catch { toast.error('Erreur mapping'); }
  };

  const handleUnmatch = async (m) => {
    try {
      await deleteModelMapping(m.model, m.storage || '');
      setMappings(prev => prev.filter(mp => !(mp.pb2b_model === m.model && mp.pb2b_storage === (m.storage || ''))));
      toast.success('Mapping supprime');
    } catch { toast.error('Erreur'); }
  };

  const handleAddQuery = async () => {
    if (!newQuery.trim()) return;
    try {
      const res = await addArgusQuery(newQuery.trim());
      setQueries(res.queries);
      setNewQuery('');
      toast.success(`"${newQuery.trim()}" ajoute aux modeles a scanner`);
    } catch { toast.error('Erreur'); }
  };

  const handleRemoveQuery = async (q) => {
    try {
      const res = await removeArgusQuery(q);
      setQueries(res.queries);
      toast.success(`"${q}" supprime`);
    } catch { toast.error('Erreur'); }
  };

  const handleSaveManual = async () => {
    if (!newManual.model || !newManual.price) { toast.error('Modele et prix requis'); return; }
    setSaving(true);
    try {
      await upsertArgusManualPrice(newManual);
      const res = await getArgusManualPrices();
      setManualPrices(res.prices || []);
      setNewManual({ brand: '', model: '', storage: '', price: '' });
      setEditingIdx(null);
      toast.success('Prix manuel enregistre');
      if (pb2bConfigured) fetchMissing();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleDeleteManual = async (mp) => {
    try {
      await deleteArgusManualPrice(mp.model, mp.storage);
      setManualPrices(prev => prev.filter(p => !(p.model === mp.model && p.storage === mp.storage)));
      toast.success('Prix supprime');
    } catch { toast.error('Erreur'); }
  };

  const handleEditManual = (mp, idx) => {
    setNewManual({ brand: mp.brand || '', model: mp.model, storage: mp.storage || '', price: String(mp.price) });
    setEditingIdx(idx);
    setActiveTab('manual');
  };

  const prefillManual = (m) => {
    setNewManual({ brand: m.brand || '', model: m.model || '', storage: m.storage || '', price: '' });
    setActiveTab('manual');
    setTimeout(() => document.querySelector('[data-testid="manual-price"]')?.focus(), 100);
  };

  const addAsQuery = async (m) => {
    const q = `${m.brand || ''} ${m.model || ''}`.trim();
    try {
      const res = await addArgusQuery(q);
      setQueries(res.queries);
      toast.success(`"${q}" ajoute. Cliquez "Actualiser catalogue" pour scanner.`);
    } catch { toast.error('Erreur'); }
  };

  // Filtered catalog models for dropdown
  const filteredCatalogModels = useMemo(() => {
    // Strip 4G/5G/LTE from search since catalog merges these variants
    const search = argusSearch.toLowerCase().trim().replace(/\s*(4g|5g|lte)\b/gi, '').trim();
    const unique = [];
    const seen = new Set();
    for (const l of catalogLines) {
      const key = `${l.model}|||${l.storage || ''}`;
      if (!seen.has(key)) {
        seen.add(key);
        if (!search || l.model.toLowerCase().includes(search) || (l.storage || '').toLowerCase().includes(search)) {
          unique.push(l);
        }
      }
    }
    // Sort: exact model match first, then starts-with, then contains
    return unique.sort((a, b) => {
      if (!search) return a.model.localeCompare(b.model);
      const aModel = a.model.toLowerCase();
      const bModel = b.model.toLowerCase();
      const aExact = aModel === search || aModel.startsWith(search + ' ');
      const bExact = bModel === search || bModel.startsWith(search + ' ');
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      const aStarts = aModel.startsWith(search);
      const bStarts = bModel.startsWith(search);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return aModel.localeCompare(bModel);
    });
  }, [catalogLines, argusSearch]);

  // Classify missing models
  const classifiedModels = useMemo(() => {
    const matched = [];
    const unmatched = [];
    const withManual = [];
    const queriesLower = queries.map(q => q.toLowerCase());
    for (const m of missingModels) {
      const mapping = getMapping(m);
      const manual = getManualPrice(m);
      // Check if model has a scanner query
      const modelFull = `${m.brand || ''} ${m.model || ''}`.trim().toLowerCase().replace(/\s*(4g|5g|lte)\b/gi, '').trim();
      // Check if model has a scanner query (use word boundary matching to avoid "iPhone X" matching "iPhone XR")
      const hasQuery = queriesLower.some(q => {
        const mModel = (m.model || '').toLowerCase().trim().replace(/\s*(4g|5g|lte)\b/gi, '').trim();
        // Query covers this model's family (e.g., query "iphone x" found in "apple iphone x")
        if (modelFull.includes(q)) return true;
        // Exact model match with the query
        if (q === mModel || q === modelFull) return true;
        // Query starts with model name + space (e.g., query "iPhone X 64Go" for model "iPhone X")
        if (q.startsWith(mModel + ' ')) return true;
        return false;
      });
      if (mapping) {
        const argusLine = getArgusPrice(mapping.argus_model, mapping.argus_storage);
        matched.push({ ...m, mapping, argusPrice: argusLine?.avg_price });
      } else if (manual) {
        withManual.push({ ...m, manualPrice: manual.price });
      } else {
        // Check if model exists in the Argus catalog (scanned but not yet matched)
        const inCatalog = catalogLines.some(cl => {
          const clModel = cl.model.toLowerCase();
          const mModel = (m.model || '').toLowerCase().replace(/\s*(4g|5g|lte)\b/gi, '').trim();
          // Exact match or exact match with space after (avoid "iPhone X" matching "iPhone XR")
          return clModel === mModel || clModel.startsWith(mModel + ' ') || mModel === clModel || mModel.startsWith(clModel + ' ');
        });
        unmatched.push({ ...m, hasQuery, inCatalog });
      }
    }
    // Sort unmatched: "Dans catalogue" first, then alphabetically
    unmatched.sort((a, b) => {
      if (a.inCatalog && !b.inCatalog) return -1;
      if (!a.inCatalog && b.inCatalog) return 1;
      const aName = `${a.brand} ${a.model}`.toLowerCase();
      const bName = `${b.brand} ${b.model}`.toLowerCase();
      return aName.localeCompare(bName);
    });
    return { matched, unmatched, withManual };
  }, [missingModels, mappings, manualPrices, catalogLines, queries]);

  if (loading) return (
    <Card className="border-slate-200 mb-4"><CardContent className="py-6 flex items-center justify-center gap-2">
      <Loader2 className="w-5 h-5 animate-spin text-emerald-500" /><span className="text-sm text-slate-500">Chargement parametres...</span>
    </CardContent></Card>
  );

  return (
    <Card className="border-slate-200 mb-4 bg-slate-50/50" data-testid="argus-settings-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Settings className="w-4 h-4 text-slate-500" />Parametres Argus
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0"><X className="w-4 h-4" /></Button>
        </div>
        {/* Tab nav */}
        <div className="flex gap-1 mt-2">
          {[
            { id: 'matching', label: `Matching Phone B2B (${missingModels.length})`, icon: Link },
            { id: 'manual', label: `Prix manuels (${manualPrices.length})`, icon: Tag },
            { id: 'scanner', label: `Scanner (${queries.length})`, icon: Search },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-medium transition-colors ${activeTab === tab.id ? 'bg-emerald-100 text-emerald-800' : 'text-slate-500 hover:bg-slate-100'}`}
              data-testid={`tab-${tab.id}`}>
              <tab.icon className="w-3 h-3" />{tab.label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">

        {/* Phone B2B Status */}
        <div className="p-2 bg-white rounded-lg border border-slate-200 flex items-center gap-2">
          <Link className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-xs font-medium text-slate-600">Phone B2B</span>
          {pb2bConfigured ? (
            <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[9px]">Connecte</Badge>
          ) : (
            <Badge className="bg-red-100 text-red-700 border-0 text-[9px]">Non configure</Badge>
          )}
        </div>

        {/* TAB: Matching */}
        {activeTab === 'matching' && pb2bConfigured && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold text-slate-700">
                Modeles Phone B2B a matcher ({classifiedModels.unmatched.length} non matches)
              </h4>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => { onRefresh(); toast.success('Actualisation du catalogue lancee (~30s)'); }} className="h-6 text-[10px] gap-1 text-emerald-700" data-testid="refresh-catalog-from-settings-btn">
                  <RefreshCw className="w-3 h-3" />Actualiser catalogue
                </Button>
                <Button variant="ghost" size="sm" onClick={fetchMissing} disabled={loadingMissing} className="h-6 text-[10px] gap-1" data-testid="refresh-missing-btn">
                  <RefreshCw className={`w-3 h-3 ${loadingMissing ? 'animate-spin' : ''}`} />Actualiser liste
                </Button>
              </div>
            </div>

            {loadingMissing ? (
              <div className="flex items-center gap-2 py-4 justify-center"><Loader2 className="w-4 h-4 animate-spin text-emerald-500" /></div>
            ) : (
              <>
                {/* Unmatched models */}
                {classifiedModels.unmatched.length > 0 && (
                  <div className="p-3 bg-amber-50/60 rounded-lg border border-amber-200">
                    <h5 className="text-[11px] font-semibold text-amber-800 mb-2 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />Sans prix Argus ({classifiedModels.unmatched.length})
                    </h5>
                    <Input placeholder="Rechercher un modele (ex: iPhone X)..." value={pb2bSearch}
                      onChange={e => setPb2bSearch(e.target.value)}
                      className="h-7 text-xs mb-2" data-testid="pb2b-search-input" />
                    {(() => {
                      const search = pb2bSearch.toLowerCase().trim();
                      const filtered = search
                        ? classifiedModels.unmatched.filter(m => {
                            const full = `${m.brand || ''} ${m.model || ''} ${m.storage || ''}`.toLowerCase();
                            return full.includes(search);
                          })
                        : classifiedModels.unmatched;
                      return (
                        <>
                          {search && <p className="text-[10px] text-amber-700 mb-1">{filtered.length} resultat(s) pour "{pb2bSearch}"</p>}
                          <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                            {filtered.map((m, i) => (
                        <div key={`u-${m.model}-${m.storage_gb}`} className="bg-white rounded px-2.5 py-2 border border-amber-100" data-testid={`unmatched-${i}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Smartphone className="w-3.5 h-3.5 text-slate-400" />
                              <span className="text-xs font-medium text-slate-800">{m.brand} {m.model}</span>
                              {m.storage && <Badge variant="outline" className="text-[9px]">{m.storage}</Badge>}
                              <Badge variant="outline" className="text-[9px] text-slate-500">{m.stock_count} en stock</Badge>
                              {m.inCatalog && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[9px]">Dans catalogue</Badge>}
                              {m.hasQuery && !m.inCatalog && <Badge className="bg-blue-100 text-blue-700 border-0 text-[9px]">Scanne</Badge>}
                            </div>
                            <div className="flex items-center gap-1">
                              <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1 text-blue-700 border-blue-200"
                                onClick={() => {
                                  const isToggle = matchingModel?.model === m.model && matchingModel?.storage === m.storage;
                                  setMatchingModel(isToggle ? null : m);
                                  setSelectedArgus('');
                                  // Auto-fill search with model name, stripping 4G/5G/LTE since catalog merges them
                                  setArgusSearch(isToggle ? '' : (m.model || '').replace(/\s*(4G|5G|LTE)\b/gi, '').trim());
                                }}
                                data-testid={`match-btn-${i}`}>
                                <ArrowRight className="w-3 h-3" />Matcher
                              </Button>
                              <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1 text-indigo-700 border-indigo-200" onClick={() => addAsQuery(m)} data-testid={`scan-btn-${i}`}>
                                <Search className="w-3 h-3" />Scanner
                              </Button>
                              <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1 text-violet-700 border-violet-200" onClick={() => prefillManual(m)} data-testid={`manual-btn-${i}`}>
                                <Pencil className="w-3 h-3" />Prix manuel
                              </Button>
                            </div>
                          </div>
                          {/* Matching selector */}
                          {matchingModel?.model === m.model && matchingModel?.storage === m.storage && (
                            <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                              <p className="text-[10px] text-blue-700 mb-1.5 font-medium">Selectionnez le modele Argus correspondant :</p>
                              <div className="flex gap-2 mb-1.5">
                                <Input placeholder="Rechercher dans le catalogue..." value={argusSearch}
                                  onChange={e => setArgusSearch(e.target.value)}
                                  className="h-7 text-xs flex-1" data-testid="argus-search-input" />
                              </div>
                              <div className="max-h-[350px] overflow-y-auto space-y-0.5">
                                {filteredCatalogModels.slice(0, 50).map((cl) => {
                                  const val = `${cl.model}|||${cl.storage || ''}`;
                                  return (
                                    <button key={val} onClick={async () => {
                                      // Direct match on click - no need for separate confirm
                                      try {
                                        await upsertModelMapping({
                                          pb2b_model: m.model,
                                          pb2b_storage: m.storage || '',
                                          pb2b_brand: m.brand || '',
                                          argus_model: cl.model,
                                          argus_storage: cl.storage || '',
                                        });
                                        const res = await getModelMappings();
                                        setMappings(res.mappings || []);
                                        setMatchingModel(null);
                                        setArgusSearch('');
                                        toast.success(`${m.model} → ${cl.model} ${cl.storage || ''} (${cl.avg_price} EUR)`);
                                      } catch { toast.error('Erreur mapping'); }
                                    }}
                                      className="w-full text-left px-2 py-1.5 rounded text-xs flex items-center justify-between transition-colors hover:bg-blue-200 hover:text-blue-900 text-slate-700"
                                      data-testid={`argus-option-${cl.model}-${cl.storage}`}>
                                      <span className="font-medium">{cl.model} <span className="text-slate-500">{cl.storage}</span></span>
                                      <span className="text-emerald-700 font-semibold">{cl.avg_price} EUR</span>
                                    </button>
                                  );
                                })}
                                {filteredCatalogModels.length === 0 && (
                                  <div className="py-3 text-center space-y-2">
                                    <p className="text-[10px] text-red-600 font-semibold">"{argusSearch}" n'est pas dans le catalogue Argus.</p>
                                    <p className="text-[10px] text-slate-600">Etapes pour l'ajouter :</p>
                                    <p className="text-[10px] text-blue-700">1. Fermez ce menu et cliquez <strong>"Scanner"</strong> sur ce modele</p>
                                    <p className="text-[10px] text-blue-700">2. Puis cliquez <strong>"Actualiser catalogue"</strong> (attendre ~30s)</p>
                                    <p className="text-[10px] text-blue-700">3. Revenez ici pour matcher</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                          {filtered.length === 0 && search && (
                            <p className="text-[10px] text-amber-500 py-2 text-center">Aucun modele PhoneB2B ne correspond a "{pb2bSearch}"</p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Matched models */}
                {classifiedModels.matched.length > 0 && (
                  <div className="p-3 bg-emerald-50/60 rounded-lg border border-emerald-200">
                    <h5 className="text-[11px] font-semibold text-emerald-800 mb-2">Matches ({classifiedModels.matched.length})</h5>
                    <div className="space-y-1 max-h-[200px] overflow-y-auto">
                      {classifiedModels.matched.map((m, i) => (
                        <div key={`m-${m.model}-${m.storage_gb}`} className="flex items-center justify-between bg-white rounded px-2.5 py-1.5 border border-emerald-100" data-testid={`matched-${i}`}>
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-slate-600">{m.brand} {m.model}</span>
                            {m.storage && <Badge variant="outline" className="text-[9px]">{m.storage}</Badge>}
                            <ArrowRight className="w-3 h-3 text-emerald-500" />
                            <span className="font-medium text-emerald-800">{m.mapping.argus_model}</span>
                            {m.mapping.argus_storage && <Badge variant="outline" className="text-[9px]">{m.mapping.argus_storage}</Badge>}
                            {m.argusPrice !== undefined && <span className="font-semibold text-emerald-700">{m.argusPrice} EUR</span>}
                          </div>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={() => handleUnmatch(m)} data-testid={`unmatch-${i}`}>
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Models with manual price */}
                {classifiedModels.withManual.length > 0 && (
                  <div className="p-3 bg-violet-50/60 rounded-lg border border-violet-200">
                    <h5 className="text-[11px] font-semibold text-violet-800 mb-2 flex items-center gap-1">
                      <Tag className="w-3 h-3" />Prix manuels ({classifiedModels.withManual.length})
                    </h5>
                    <div className="space-y-1 max-h-[150px] overflow-y-auto">
                      {classifiedModels.withManual.map((m, i) => (
                        <div key={`mp-${m.model}-${m.storage_gb}`} className="flex items-center justify-between bg-white rounded px-2.5 py-1.5 border border-violet-100">
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-slate-600">{m.brand} {m.model}</span>
                            {m.storage && <Badge variant="outline" className="text-[9px]">{m.storage}</Badge>}
                            <Badge className="bg-violet-100 text-violet-700 border-0 text-[9px]">Manuel</Badge>
                            <span className="font-semibold text-violet-700">{m.manualPrice} EUR</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {missingModels.length === 0 && !loadingMissing && (
                  <p className="text-xs text-slate-400 py-2 text-center">Aucun modele Phone B2B a traiter</p>
                )}
              </>
            )}
          </div>
        )}

        {/* TAB: Manual Prices */}
        {activeTab === 'manual' && (
          <div>
            <h4 className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
              <Tag className="w-3.5 h-3.5" />Prix manuels ({manualPrices.length})
            </h4>
            {manualPrices.length > 0 && (
              <div className="overflow-x-auto mb-3">
                <table className="w-full text-xs" data-testid="manual-prices-table">
                  <thead><tr className="border-b border-slate-200 text-left">
                    <th className="py-1.5 px-2 text-slate-500 font-medium">Marque</th>
                    <th className="py-1.5 px-2 text-slate-500 font-medium">Modele</th>
                    <th className="py-1.5 px-2 text-slate-500 font-medium">Stockage</th>
                    <th className="py-1.5 px-2 text-slate-500 font-medium text-right">Prix</th>
                    <th className="py-1.5 px-2 text-slate-500 font-medium w-20"></th>
                  </tr></thead>
                  <tbody>
                    {manualPrices.map((mp, i) => (
                      <tr key={`${mp.model}-${mp.storage}`} className="border-b border-slate-100 hover:bg-white/80">
                        <td className="py-1.5 px-2 text-slate-600">{mp.brand || '-'}</td>
                        <td className="py-1.5 px-2 font-medium text-slate-800">{mp.model}</td>
                        <td className="py-1.5 px-2 text-slate-600">{mp.storage || '-'}</td>
                        <td className="py-1.5 px-2 text-right font-semibold text-violet-700">{mp.price} EUR</td>
                        <td className="py-1.5 px-2">
                          <div className="flex gap-1 justify-end">
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleEditManual(mp, i)} data-testid={`edit-manual-${i}`}><Pencil className="w-3 h-3" /></Button>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" onClick={() => handleDeleteManual(mp)} data-testid={`del-manual-${i}`}><X className="w-3 h-3" /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="grid grid-cols-5 gap-2 items-end">
              <div><label className="text-[10px] text-slate-400 block mb-0.5">Marque</label>
                <Input placeholder="Apple" value={newManual.brand} onChange={e => setNewManual(p => ({...p, brand: e.target.value}))} className="h-8 text-sm" data-testid="manual-brand" /></div>
              <div><label className="text-[10px] text-slate-400 block mb-0.5">Modele *</label>
                <Input placeholder="iPhone 12 Pro" value={newManual.model} onChange={e => setNewManual(p => ({...p, model: e.target.value}))} className="h-8 text-sm" data-testid="manual-model" /></div>
              <div><label className="text-[10px] text-slate-400 block mb-0.5">Stockage</label>
                <Input placeholder="128 Go" value={newManual.storage} onChange={e => setNewManual(p => ({...p, storage: e.target.value}))} className="h-8 text-sm" data-testid="manual-storage" /></div>
              <div><label className="text-[10px] text-slate-400 block mb-0.5">Prix (EUR) *</label>
                <Input type="number" step="0.01" placeholder="199.99" value={newManual.price} onChange={e => setNewManual(p => ({...p, price: e.target.value}))}
                  onKeyDown={e => e.key === 'Enter' && handleSaveManual()} className="h-8 text-sm" data-testid="manual-price" /></div>
              <Button size="sm" onClick={handleSaveManual} disabled={saving} className="h-8 text-xs gap-1 bg-violet-500 hover:bg-violet-600" data-testid="save-manual-btn">
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {editingIdx !== null ? 'Modifier' : 'Ajouter'}
              </Button>
            </div>
          </div>
        )}

        {/* TAB: Scanner */}
        {activeTab === 'scanner' && (
          <div>
            <h4 className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
              <Filter className="w-3.5 h-3.5" />Modeles a scanner ({queries.length})
            </h4>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {queries.map(q => (
                <Badge key={q} variant="outline" className="text-xs gap-1 pr-1 hover:bg-red-50 cursor-pointer group" data-testid={`query-tag-${q}`}>
                  {q}
                  <button onClick={() => handleRemoveQuery(q)} className="ml-1 opacity-40 group-hover:opacity-100 hover:text-red-600 transition-opacity"><X className="w-3 h-3" /></button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input placeholder="Ajouter un modele (ex: Samsung Galaxy A55)" value={newQuery}
                onChange={e => setNewQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddQuery()}
                className="h-8 text-sm" data-testid="add-query-input" />
              <Button size="sm" onClick={handleAddQuery} className="h-8 text-xs gap-1 bg-emerald-500 hover:bg-emerald-600" data-testid="add-query-btn">
                <Plus className="w-3.5 h-3.5" />Ajouter
              </Button>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">Le scan du catalogue se lance automatiquement apres ajout d'un modele</p>
          </div>
        )}

      </CardContent>
    </Card>
  );
}
